import React, { useState } from 'react';
import { Send, Download, FileCheck, FileText, Loader } from 'lucide-react';
import { Button, Spinner } from 'react-bootstrap';
import { getApiUrl } from '../../../../../utils/ApiUtils';
import axios from '../../../../../services/AxiosInstance';
import { toast } from 'sonner';

const SubmissionStep = ({
    vatPeriod,
    companyId,
    vatPeriodId,
    vatData,
    totalVat,
    companyOrgNr,
    operationInProgress,
    isPreviewMode,
    markStepComplete,
    completionStatus,
    onReportGenerated
}) => {
    const [generating, setGenerating] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [confirming, setConfirming] = useState(false);

    const handleGenerateReport = async () => {
        setGenerating(true);

        try {
            // Prepare VAT report data
            const vatReportData = {
                orgNr: companyOrgNr,
                period: `${new Date(vatPeriod.end_date).getFullYear()}${String(new Date(vatPeriod.end_date).getMonth() + 1).padStart(2, '0')}`,
                vatData: vatData,
                totalVat: totalVat
            };

            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/generate-report`),
                { vatReportData: vatReportData },
                { withCredentials: true }
            );

            if (response.data.success) {
                toast.success("Momsrapport genererad framgångsrikt");
                if (onReportGenerated) onReportGenerated();
            } else {
                toast.error("Kunde inte generera momsrapporten");
            }
        } catch (error) {
            console.error('Error generating VAT report:', error);
            toast.error(error.response?.data?.message || "Ett fel uppstod vid generering av rapporten");
        } finally {
            setGenerating(false);
        }
    };

    const handleDownloadReport = async () => {
        setDownloading(true);

        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/download/vat-report/${vatPeriodId}`),
                {
                    withCredentials: true,
                    responseType: 'blob' // Important: tells axios to handle binary data
                }
            );

            // Create a blob URL and trigger download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', vatPeriod.file_name || 'momsrapport.xml');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success("Momsrapport nedladdad");
        } catch (error) {
            console.error('Error downloading VAT report:', error);
            toast.error(error.response?.data?.message || "Ett fel uppstod vid nedladdning av rapporten");
        } finally {
            setDownloading(false);
        }
    };

    const handleConfirmSubmission = async () => {
        setConfirming(true);

        try {
            await markStepComplete('submission');
            toast.success("Inlämning till Skatteverket bekräftad");
        } catch (error) {
            console.error('Error confirming submission:', error);
            toast.error("Kunde inte bekräfta inlämningen");
        } finally {
            setConfirming(false);
        }
    };

    return (
        <div className="step-container">
            <div className="step-header">
                <div className="d-flex align-items-center">
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                            width: '48px',
                            height: '48px',
                            backgroundColor: '#3b82f6',
                            color: 'white'
                        }}
                    >
                        <Send size={24} />
                    </div>
                    <div>
                        <h3 className="mb-1">Skicka Momsrapport</h3>
                        <p className="text-muted mb-0">Generera och skicka rapporten till Skatteverket</p>
                    </div>
                </div>
            </div>

            <div className="step-content">
                <p className="mb-4">
                    Generera XML-filen för momsrapporten och ladda upp den på Skatteverkets webbplats.
                </p>

                {!vatPeriod?.file_name ? (
                    <Button
                        variant="primary"
                        onClick={handleGenerateReport}
                        disabled={generating || operationInProgress || isPreviewMode}
                        className="d-inline-flex align-items-center"
                    >
                        {generating ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Genererar rapport...
                            </>
                        ) : (
                            <>
                                <FileText size={16} className="me-2" />
                                Generera momsrapport
                            </>
                        )}
                    </Button>
                ) : (
                    <div>
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <Button
                                variant="primary"
                                onClick={handleDownloadReport}
                                disabled={downloading || operationInProgress || isPreviewMode}
                                className="d-inline-flex align-items-center"
                            >
                                {downloading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Laddar ner...
                                    </>
                                ) : (
                                    <>
                                        <Download size={16} className="me-2" />
                                        Ladda ner XML-fil
                                    </>
                                )}
                            </Button>

                            {!completionStatus?.submission_completed && (
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleGenerateReport}
                                    disabled={generating || operationInProgress || isPreviewMode}
                                    className="d-inline-flex align-items-center"
                                >
                                    {generating ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : (
                                        <>
                                            <FileText size={14} className="me-1" />
                                            Generera om
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>

                        <p className="text-muted small mb-3">
                            Fil: {vatPeriod.file_name}
                        </p>

                        {!completionStatus?.submission_completed ? (
                            <div className="alert alert-info mb-3" style={{ backgroundColor: '#e0f2fe', border: '1px solid #7dd3fc' }}>
                                <p className="mb-2 fw-semibold">Nästa steg:</p>
                                <ol className="mb-2 ps-3">
                                    <li>Ladda ner XML-filen ovan</li>
                                    <li>Gå till <a href="https://www.skatteverket.se" target="_blank" rel="noopener noreferrer">Skatteverket.se</a></li>
                                    <li>Logga in och ladda upp XML-filen</li>
                                    <li>När du har skickat in rapporten, klicka på knappen nedan</li>
                                </ol>
                                <Button
                                    variant="success"
                                    onClick={handleConfirmSubmission}
                                    disabled={confirming || operationInProgress || isPreviewMode}
                                    className="d-inline-flex align-items-center"
                                >
                                    {confirming ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Bekräftar...
                                        </>
                                    ) : (
                                        <>
                                            <FileCheck size={16} className="me-2" />
                                            Bekräfta inlämning till Skatteverket
                                        </>
                                    )}
                                </Button>
                            </div>
                        ) : (
                            <div className="alert alert-success">
                                <div className="d-flex align-items-center">
                                    <FileCheck size={20} className="me-2" />
                                    <span className="fw-semibold">Inlämning till Skatteverket bekräftad</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubmissionStep;